package one.healthium.log_water.ui.internal

import co.touchlab.kermit.Logger
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.update
import one.healthium.base.preferences.AppPreferences
import one.healthium.base.utils.ozToLitres
import one.healthium.domain.user.UserInteractor
import one.healthium.domain.user.UserModel
import one.healthium.domain.user.models.WaterLogModel
import one.healthium.ui.common.vm.BaseViewModel
import kotlin.math.max

internal class LogWaterViewModel(
    private val userInteractor: UserInteractor,
    appPreferences: AppPreferences,
) : BaseViewModel() {

    private val _state = MutableStateFlow(LogWaterUiState(isMetric = appPreferences.isMetric))
    val state = _state.asStateFlow()

    init {
        userInteractor.observeUser()
            .onEach { user ->
                val todayWaterLog = user.todayWaterLog
                _state.value = _state.value.copy(
                    waterIntake = todayWaterLog.toUiModel(_state.value, user)
                )
            }
            .catch {
                // TODO
            }
            .launch()

        appPreferences.isMetricFlow
            .onEach { isMetric ->
                _state.update { state -> state.copy(isMetric = isMetric) }
            }
            .launch()
    }

    fun onWaterIntakeEditFinished(taskId: String?) {
        val additionValue = _state.value.waterIntake?.additionValue

        if (additionValue != null && additionValue != 0f) {
            launch {
                try {
                    userInteractor.addWaterIntake(additionValue, taskId)

                    _state.update { state ->
                        state.copy(
                            waterIntake = state.waterIntake?.copy(additionValue = 0f)
                        )
                    }
                } catch (e: Exception) {
                    // TODO show error
                    Logger.e("addWaterIntake error", e)
                    _state.update { state ->
                        state.copy(
                            waterIntake = state.waterIntake?.copy(
                                additionValue = additionValue,
                            )
                        )
                    }
                }
            }
        }
    }

    fun onIncreaseWaterIntake() {
        _state.update { state ->
            state.copy(
                waterIntake = state.waterIntake?.copy(
                    additionValue = state.waterIntake.additionValue + waterIntakeStep(state.isMetric)
                )
            )
        }
    }

    fun onDecreaseWaterIntake() {
        if (_state.value.waterIntake?.value == 0f) return

        _state.update { state ->
            state.copy(
                waterIntake = state.waterIntake?.copy(
                    additionValue = max(
                        -state.waterIntake.currentValue,
                        state.waterIntake.additionValue - waterIntakeStep(state.isMetric),
                    ),
                )
            )
        }
    }
}

private fun waterIntakeStep(isMetric: Boolean) = if (isMetric) 0.15f else 8f.ozToLitres()

private fun WaterLogModel?.toUiModel(
    state: LogWaterUiState,
    user: UserModel,
): LogWaterUiModel {
    val dailyAmount = user.dailyWaterIntake
    val currentValue = this?.value ?: 0f
    return state.waterIntake?.copy(
        dailyAmount = dailyAmount,
        currentValue = currentValue
    ) ?: LogWaterUiModel(
        dailyAmount = dailyAmount,
        currentValue = this?.value ?: 0f,
    )
}
